<template lang="pug">
v-container(fluid)
  v-row
    v-col(cols="12")
      v-card
        v-card-title
          | Gestión de Publicidades
          v-spacer
          v-btn(
            color="primary"
            @click="createNewAd"
            dark
          )
            v-icon(left) mdi-plus
            | Nueva Publicidad
        
        v-card-text
          v-data-table(
            :headers="headers"
            :items="advertisements"
            :loading="loading"
            :items-per-page="10"
            :footer-props="{ 'items-per-page-options': [5, 10, 20, 50] }"
            class="elevation-1"
          )
            template(v-slot:item.image="{ item }")
              v-avatar(size="60")
                v-img(
                  :src="item.image.url"
                  :alt="item.title"
                  aspect-ratio="1"
                  contain
                )
            
            template(v-slot:item.active="{ item }")
              v-chip(
                :color="item.active ? 'success' : 'error'"
                small
                label
              ) {{ item.active ? 'Activo' : 'Inactivo' }}
              
            template(v-slot:item.status="{ item }")
              v-chip(
                :color="getStatusColor(item)"
                small
                label
              ) {{ getStatusText(item) }}
              
            template(v-slot:item.dateRange="{ item }")
              | {{ formatDate(item.startDate) }} - {{ formatDate(item.endDate) }}
              
            template(v-slot:item.statistics="{ item }")
              span.mr-2
                v-icon(small, color="primary") mdi-eye
                | {{ item.views }}
              span
                v-icon(small, color="success") mdi-cursor-pointer
                | {{ item.clicks }}
                
            template(v-slot:item.actions="{ item }")
              v-btn(
                icon
                small
                @click="editAdvertisement(item)"
                color="primary"
              )
                v-icon mdi-pencil
              v-btn(
                icon
                small
                @click="previewAdvertisement(item)"
                color="info"
              )
                v-icon mdi-eye
              v-btn(
                icon
                small
                @click="confirmDelete(item)"
                color="error"
              )
                v-icon mdi-delete

  // Diálogo de confirmación
  v-dialog(v-model="dialogDelete" max-width="500px")
    v-card
      v-card-title.text-h5 ¿Eliminar esta publicidad?
      v-card-text 
        | Esta acción no se puede deshacer. ¿Estás seguro de que quieres eliminar la publicidad?
        | 
        strong {{ selectedItem ? selectedItem.title : '' }}
        | ?
      v-card-actions
        v-spacer
        v-btn(color="blue darken-1" text @click="dialogDelete = false") Cancelar
        v-btn(color="error" text @click="deleteItem") Eliminar
        v-spacer
        
  // Diálogo de vista previa
  v-dialog(v-model="dialogPreview" max-width="800px")
    v-card
      v-card-title.text-h5 Vista previa de publicidad
      v-card-text
        v-carousel(
          height="350"
          hide-delimiters
        )
          v-carousel-item(
            :src="selectedItem ? selectedItem.image.url : ''"
            cover
          )
            .carousel-gradient
            v-container(class="fill-height")
              v-row(
                class="fill-height"
                align="center"
                justify="center"
              )
                v-col(
                  cols="12"
                  sm="10"
                  md="8"
                  class="text-center"
                )
                  .hero-content
                    h1.text-h5.text-sm-h4.font-weight-light.mb-2.white--text.text-left {{ selectedItem ? selectedItem.title : '' }}
                    p.text-subtitle-2.text-sm-subtitle-1.mb-4.white--text.text-left {{ selectedItem ? selectedItem.description : '' }}
                    v-btn.hero-button(
                      small
                      color="white"
                      elevation="0"
                      :ripple="false"
                      class="text-capitalize px-4"
                    )
                      span.mr-2.primary--text.text-body-2.text-sm-subtitle-1 {{ selectedItem ? selectedItem.buttonText : 'Ver más' }}
                      v-icon(color="primary" size="18") mdi-arrow-right
        .mt-4
          h3.subtitle-1 Información de la publicidad
          v-simple-table
            tbody
              tr
                td.font-weight-medium URL destino
                td 
                  a(:href="selectedItem ? selectedItem.targetUrl : '#'" target="_blank") {{ selectedItem ? selectedItem.targetUrl : '' }}
              tr
                td.font-weight-medium Período
                td {{ selectedItem ? formatDate(selectedItem.startDate) : '' }} - {{ selectedItem ? formatDate(selectedItem.endDate) : '' }}
              tr
                td.font-weight-medium Estado
                td 
                  v-chip(
                    v-if="selectedItem"
                    :color="getStatusColor(selectedItem)"
                    small
                    label
                  ) {{ selectedItem ? getStatusText(selectedItem) : '' }}
              tr
                td.font-weight-medium Estadísticas
                td 
                  span.mr-3
                    v-icon(small, color="primary") mdi-eye
                    | {{ selectedItem ? selectedItem.views : 0 }} vistas
                  span
                    v-icon(small, color="success") mdi-cursor-pointer
                    | {{ selectedItem ? selectedItem.clicks : 0 }} clicks
      v-card-actions
        v-spacer
        v-btn(color="primary" text @click="dialogPreview = false") Cerrar
</template>

<script>
import { format } from 'date-fns'
import { es } from 'date-fns/locale'

export default {
  name: 'ManagerAdvertisements',
  
  data: () => ({
    loading: false,
    advertisements: [],
    headers: [
      { text: 'Imagen', value: 'image', sortable: false },
      { text: 'Título', value: 'title' },
      { text: 'Estado', value: 'active', width: '100px' },
      { text: 'Situación', value: 'status', width: '120px' },
      { text: 'Período', value: 'dateRange', width: '200px' },
      { text: 'Prioridad', value: 'priority', width: '100px' },
      { text: 'Estadísticas', value: 'statistics', sortable: false, width: '120px' },
      { text: 'Acciones', value: 'actions', sortable: false, align: 'center', width: '120px' }
    ],
    dialogDelete: false,
    dialogPreview: false,
    selectedItem: null
  }),
  
  methods: {
    async loadAdvertisements() {
      this.loading = true
      try {
        await this.$store.dispatch('advertisements/fetchAdvertisements')
        this.advertisements = this.$store.state.advertisements.advertisements
      } catch (error) {
        this.$toast.error('Error al cargar las publicidades')
        console.error('Error cargando publicidades:', error)
      } finally {
        this.loading = false
      }
    },
    
    formatDate(dateString) {
      try {
        const date = new Date(dateString)
        return format(date, 'dd/MM/yyyy', { locale: es })
      } catch (error) {
        return dateString
      }
    },
    
    getStatusColor(item) {
      if (!item.active) return 'grey'
      
      const now = new Date()
      const startDate = new Date(item.startDate)
      const endDate = new Date(item.endDate)
      
      if (now < startDate) return 'warning' // Pendiente
      if (now > endDate) return 'error' // Vencido
      return 'success' // Activa
    },
    
    getStatusText(item) {
      if (!item.active) return 'Inactivo'
      
      const now = new Date()
      const startDate = new Date(item.startDate)
      const endDate = new Date(item.endDate)
      
      if (now < startDate) return 'Pendiente'
      if (now > endDate) return 'Vencido'
      return 'Publicándose'
    },
    
    createNewAd() {
      this.$router.push('/manager/advertisements/edit/new')
    },
    
    editAdvertisement(item) {
      this.$router.push(`/manager/advertisements/edit/${item._id}`)
    },
    
    previewAdvertisement(item) {
      this.selectedItem = item
      this.dialogPreview = true
    },
    
    confirmDelete(item) {
      this.selectedItem = item
      this.dialogDelete = true
    },
    
    async deleteItem() {
      try {
        await this.$store.dispatch('advertisements/deleteAdvertisement', this.selectedItem._id)
        this.$toast.success('Publicidad eliminada correctamente')
        this.dialogDelete = false
        this.loadAdvertisements()
      } catch (error) {
        this.$toast.error('Error al eliminar la publicidad')
        console.error('Error eliminando publicidad:', error)
      }
    }
  },
  
  created() {
    this.loadAdvertisements()
  }
}
</script>

<style scoped>
.carousel-gradient {
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background: linear-gradient(to bottom, rgba(0,0,0,0.1), rgba(0,0,0,0.7));
}

.hero-content {
  position: relative;
  z-index: 2;
}

.hero-button {
  border: 1px solid white !important;
}
</style>
